import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, gql } from '@apollo/client';

// GraphQL query for snapshot status
const EQ_SNAPSHOT_STATUS = gql`
  query EQSnapshotStatus($snapshotId: uuid!) {
    EQSnapshotStatus(where: { snapshot_id: { _eq: $snapshotId } }) {
      status
      status_data
    }
  }
`;

// Polling configuration
const POLL_INTERVALS = {
  INITIAL: 1000, // 1s
  PROCESSING: 3000, // 3s once processing detected
} as const;

const TIMEOUTS = {
  GRACE_PERIOD: 30000, // 30s grace period after analysis starts - ignore stale "completed" status
  HARD_TIMEOUT: 20 * 60 * 1000, // 20 minutes
} as const;

export interface PollingState {
  isPolling: boolean;
  isAnalyzing: boolean;
  progress: number;
  status: string; // The message to display
  statusValue: string; // The actual status value from DB (completed, failed, processing, etc.)
}

export interface PollingCallbacks {
  onComplete?: () => void;
  onFailure?: (message: string) => void;
  onTimeout?: () => void;
}

export interface StartPollingOptions extends PollingCallbacks {
  analysisStartTime?: number; // Timestamp when user clicked "Analyse" button
}

export const useSnapshotPolling = (snapshotId: string | null) => {
  const [state, setState] = useState<PollingState>({
    isPolling: false,
    isAnalyzing: false,
    progress: 0,
    status: '',
    statusValue: '',
  });

  const callbacksRef = useRef<PollingCallbacks>({});
  const timeoutRef = useRef<number | null>(null);
  const analysisStartTimeRef = useRef<number>(0); // When user clicked "Analyse"
  const pollingStartTimeRef = useRef<number>(0); // When polling actually started
  const hasSeenProgressRef = useRef<boolean>(false); // Track if we've seen actual analysis progress
  const consecutiveErrorsRef = useRef<number>(0); // Track consecutive query errors
  const lastSuccessfulFetchRef = useRef<number>(0); // Track last successful data fetch

  // GraphQL query for snapshot status
  const {
    data: snapshotData,
    startPolling,
    stopPolling,
    refetch,
    error: queryError,
  } = useQuery(EQ_SNAPSHOT_STATUS, {
    variables: { snapshotId },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: !snapshotId,
    onError: (error) => {
      console.error('Snapshot polling query error:', error);
      // Don't stop polling on error - let it retry
      // Apollo will automatically retry according to its error policy
    },
    errorPolicy: 'all', // Continue polling even if there are errors
  });

  // Reset state helper
  const resetState = useCallback(() => {
    setState({
      isPolling: false,
      isAnalyzing: false,
      progress: 0,
      status: '',
      statusValue: '',
    });
    analysisStartTimeRef.current = 0;
    pollingStartTimeRef.current = 0;
    hasSeenProgressRef.current = false;
    consecutiveErrorsRef.current = 0;
    lastSuccessfulFetchRef.current = 0;
  }, []);

  // Set analyzing state without starting polling - for immediate UI feedback
  const setAnalyzingState = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isAnalyzing: true,
      progress: 0,
      status: 'Starting analysis...',
      statusValue: 'starting',
    }));
  }, []);

  // Start polling
  const startPollingForSnapshot = useCallback(
    (options: StartPollingOptions = {}) => {
      if (state.isPolling || !snapshotId) {
        return;
      }

      const { analysisStartTime, ...callbacks } = options;

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Set up new polling session
      callbacksRef.current = callbacks;
      pollingStartTimeRef.current = Date.now();
      hasSeenProgressRef.current = false; // Reset progress tracking
      consecutiveErrorsRef.current = 0; // Reset error tracking
      lastSuccessfulFetchRef.current = Date.now(); // Initialize last successful fetch

      // Use provided analysis start time, or default to polling start time
      analysisStartTimeRef.current =
        analysisStartTime || pollingStartTimeRef.current;

      setState((prev) => ({
        ...prev,
        isPolling: true,
        isAnalyzing: true,
        progress: 0,
        status: 'Starting analysis...',
        statusValue: 'starting',
      }));

      // Set hard timeout
      timeoutRef.current = window.setTimeout(() => {
        stopPolling();
        setState((prev) => ({
          ...prev,
          isPolling: false,
          statusValue: 'timeout',
        }));
        callbacksRef.current.onTimeout?.();
        timeoutRef.current = null;
      }, TIMEOUTS.HARD_TIMEOUT);

      // Start polling - will fetch at INITIAL interval (1s)
      startPolling(POLL_INTERVALS.INITIAL);
    },
    [state.isPolling, snapshotId, startPolling, stopPolling],
  );

  // Stop polling (for completion - keeps isAnalyzing true so user can see result)
  const stopPollingForSnapshot = useCallback(() => {
    stopPolling();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setState((prev) => ({
      ...prev,
      isPolling: false,
      // Keep isAnalyzing: true so completion message stays visible
      progress: 100,
      status: 'Analysis completed successfully!',
      statusValue: 'completed',
    }));
  }, [stopPolling]);

  // Stop polling for failures (keeps isAnalyzing true so user can see error)
  const stopPollingWithError = useCallback(
    (errorMessage: string) => {
      stopPolling();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      setState((prev) => ({
        ...prev,
        isPolling: false,
        // Keep isAnalyzing: true so error message stays visible
        // Set to 100% to indicate the process is complete (even though it failed)
        progress: 100,
        status: errorMessage,
        statusValue: 'failed',
      }));
    },
    [stopPolling],
  );

  // Monitor for query errors and handle persistent failures
  useEffect(() => {
    if (!state.isPolling) {
      return;
    }

    if (queryError) {
      consecutiveErrorsRef.current += 1;
      console.warn(
        `Snapshot polling error (${consecutiveErrorsRef.current} consecutive):`,
        queryError,
      );

      // After 10 consecutive errors, consider polling failed
      if (consecutiveErrorsRef.current >= 10) {
        console.error(
          'Too many consecutive polling errors, stopping polling',
          queryError,
        );
        stopPollingWithError(
          'Network connection issue. Please check your connection and try again.',
        );
        callbacksRef.current.onFailure?.(
          'Network connection issue during polling',
        );
      }
    }
  }, [queryError, state.isPolling, stopPollingWithError]);

  // Handle snapshot status updates
  useEffect(() => {
    if (!state.isPolling || !snapshotData?.EQSnapshotStatus?.[0]) {
      return;
    }

    // Reset error counter on successful fetch
    consecutiveErrorsRef.current = 0;
    lastSuccessfulFetchRef.current = Date.now();

    const status = snapshotData.EQSnapshotStatus[0];
    const currentStatus = status.status;
    const progress = status.status_data?.progress_percent ?? 0;
    const message = status.status_data?.message || currentStatus;

    // Track if we've seen actual analysis progress (not terminal states)
    if (currentStatus === 'processing' || currentStatus === 'in_progress') {
      hasSeenProgressRef.current = true;
    }

    // Check if we're in the grace period (30s after analysis button was clicked)
    // analysisStartTimeRef will be 0 on page refresh or if no analysisStartTime was provided
    const hasAnalysisStartTime = analysisStartTimeRef.current > 0;
    const timeSinceAnalysisStart = Date.now() - analysisStartTimeRef.current;
    const inGracePeriod =
      hasAnalysisStartTime && timeSinceAnalysisStart < TIMEOUTS.GRACE_PERIOD;

    // During grace period (<30s since button click):
    // Ignore completed/failed ONLY if we haven't seen actual progress yet (likely stale)
    // Once we see progress, any subsequent completed/failed is real
    const shouldIgnoreTerminalStatus =
      inGracePeriod &&
      (currentStatus === 'completed' || currentStatus === 'failed') &&
      !hasSeenProgressRef.current;

    if (shouldIgnoreTerminalStatus) {
      setState((prev) => ({
        ...prev,
        progress: 0,
        status: 'Starting analysis...',
        statusValue: 'starting',
      }));
      // Don't stop polling or call callbacks - still waiting for analysis to start
      return;
    }

    // Show the actual status from DB
    setState((prev) => ({
      ...prev,
      progress: progress,
      status: message,
      statusValue: currentStatus, // Store the actual DB status value
    }));

    // Switch to slower polling once processing is detected
    if (currentStatus === 'processing' || currentStatus === 'in_progress') {
      startPolling(POLL_INTERVALS.PROCESSING);
    }

    // Handle completion
    if (currentStatus === 'completed') {
      stopPollingForSnapshot();
      callbacksRef.current.onComplete?.();
      return;
    }

    // Handle failure
    if (currentStatus === 'failed') {
      stopPollingWithError(message);
      callbacksRef.current.onFailure?.(message);
      return;
    }
  }, [
    snapshotData,
    state.isPolling,
    startPolling,
    stopPollingForSnapshot,
    stopPollingWithError,
  ]);

  // Check for existing analysis on mount
  useEffect(() => {
    if (snapshotId && !state.isPolling && refetch) {
      // Check if there's already an analysis in progress
      refetch()
        .then((result) => {
          const status = result.data?.EQSnapshotStatus?.[0];
          if (
            status &&
            (status.status === 'processing' || status.status === 'in_progress')
          ) {
            // Resume polling for existing analysis
            startPollingForSnapshot({
              onComplete: () => {
                // Analysis completed while component was unmounted
              },
              onFailure: (_message: string) => {
                // Analysis failed while component was unmounted
              },
              onTimeout: () => {
                // Analysis timed out while component was unmounted
              },
            });
          }
        })
        .catch(() => {
          // Ignore errors when checking initial status
        });
    }
  }, [snapshotId, state.isPolling, refetch, startPollingForSnapshot]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (stopPolling) {
        stopPolling();
      }
    };
  }, [stopPolling]);

  return {
    ...state,
    startPolling: startPollingForSnapshot,
    stopPolling: stopPollingForSnapshot,
    resetState,
    setAnalyzingState,
  };
};
